/**
 * Resilient Image Component
 *
 * Handles JWT token expiration issues with automatic URL refresh
 *
 * Primary responsibilities:
 * - Display images with automatic fallback on URL expiration
 * - Refresh signed URLs when they fail to load
 * - Provide loading states and error handling
 * - Cache refreshed URLs to prevent unnecessary requests
 */

'use client'

import React, { CSSProperties, useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { getSafeRenderableImageUrl } from '@/lib/media/safe-url'
import { signedUrlManager } from '@/lib/supabase/signed-url-manager'
import { cn } from '@/lib/utils'

// If the browser already has this URL decoded (e.g. a caller preloaded it
// before committing the src change — see components/messages/message.tsx's
// blob: -> signed-URL swap), skip the loading-state flash entirely instead
// of always resetting to "loading" on every src change.
function isImageAlreadyCached(url: string) {
  if (typeof window === 'undefined') return false

  const probe = new window.Image()
  probe.src = url
  return probe.complete
}

interface ResilientImageProps {
  src: unknown
  alt: string
  width?: number
  height?: number
  className?: string
  fill?: boolean
  preserveAspectRatio?: boolean
  priority?: boolean
  loading?: 'lazy' | 'eager'
  style?: CSSProperties
  onError?: () => void
  onLoad?: () => void
  onClick?: () => void
  fallbackSrc?: string
  retryCount?: number
  maxRetries?: number
}

export const ResilientImage: React.FC<ResilientImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  fill = false,
  preserveAspectRatio = false,
  priority = false,
  loading = 'lazy',
  style,
  onError,
  onLoad,
  onClick,
  fallbackSrc,
  retryCount = 0,
  maxRetries = 3
}) => {
  const normalizedSrc = getSafeRenderableImageUrl(src)
  const [currentSrc, setCurrentSrc] = useState(normalizedSrc || '')
  const [isLoading, setIsLoading] = useState(() =>
    normalizedSrc ? !isImageAlreadyCached(normalizedSrc) : false
  )
  const [hasError, setHasError] = useState(!normalizedSrc)
  const [retryAttempts, setRetryAttempts] = useState(retryCount)

  // Check if the src is a Supabase signed URL that might need refreshing
  const isSupabaseUrl = currentSrc.includes('supabase') && currentSrc.includes('token=')
  const isDataUrl = currentSrc.startsWith('data:')
  const isHttpUrl = currentSrc.startsWith('http')

  const handleImageError = useCallback(async () => {
    if (hasError || retryAttempts >= maxRetries) {
      setHasError(true)
      onError?.()
      return
    }

    // Only attempt refresh for Supabase URLs
    if (isSupabaseUrl && retryAttempts < maxRetries) {
      try {
        setIsLoading(true)
        setRetryAttempts((prev) => prev + 1)

        // Extract file path and bucket from the URL
        const url = new URL(currentSrc)
        const pathMatch = url.pathname.match(
          /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/
        )

        if (pathMatch) {
          const [, bucket, filePath] = pathMatch
          const refreshedUrl = await signedUrlManager.getSignedUrl(
            decodeURIComponent(filePath),
            bucket
          )
          setCurrentSrc(refreshedUrl)
          setHasError(false)
        } else {
          // If we can't parse the URL, try the original src
          const refreshedUrl = await signedUrlManager.getSignedUrl(currentSrc, 'message_images')
          setCurrentSrc(refreshedUrl)
          setHasError(false)
        }
      } catch (error) {
        console.error('Failed to refresh signed URL:', error)
        setHasError(true)
        onError?.()
      }
    } else {
      setHasError(true)
      onError?.()
    }
  }, [currentSrc, hasError, retryAttempts, maxRetries, isSupabaseUrl, onError])

  const handleImageLoad = useCallback(() => {
    setIsLoading(false)
    setHasError(false)
    onLoad?.()
  }, [onLoad])

  // Reset state when src changes
  useEffect(() => {
    setCurrentSrc(normalizedSrc || '')
    setIsLoading(normalizedSrc ? !isImageAlreadyCached(normalizedSrc) : false)
    setHasError(!normalizedSrc)
    setRetryAttempts(retryCount)
  }, [normalizedSrc, retryCount])

  if (!normalizedSrc) {
    return fallbackSrc ? (
      <Image
        src={fallbackSrc}
        alt={alt}
        width={width}
        height={height}
        fill={fill}
        priority={priority}
        loading={loading}
        className={className}
        style={style}
      />
    ) : (
      <div className="bg-muted/50 text-muted-foreground flex items-center justify-center">
        <div className="p-4 text-center">
          <div className="text-sm">Image unavailable</div>
        </div>
      </div>
    )
  }

  if (preserveAspectRatio) {
    const nativeImage = (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={currentSrc}
        alt={alt}
        // Without reserved width/height, an unloaded <img> renders the
        // browser's tiny default broken-image box, then pops to full size
        // once decoded — hide it until onLoad instead so the image just
        // fades in at its final size, no intermediate "square" flash. The
        // caller's own className is expected to declare the opacity
        // transition (message.tsx's transition-[...,opacity,...]) — not
        // redeclared here to avoid two conflicting transition-property values.
        className={cn(className, isLoading ? 'opacity-0' : 'opacity-100')}
        loading={loading}
        onError={isSupabaseUrl ? handleImageError : onError}
        onLoad={handleImageLoad}
        onClick={onClick}
        style={style}
      />
    )

    if (hasError) {
      return fallbackSrc ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={fallbackSrc}
          alt={alt}
          className={className}
          loading={loading}
          onClick={onClick}
          style={style}
        />
      ) : (
        <div className="bg-muted/50 text-muted-foreground flex items-center justify-center">
          <div className="p-4 text-center">
            <div className="text-sm">Image unavailable</div>
            {retryAttempts < maxRetries && (
              <button
                onClick={handleImageError}
                className="text-primary mt-2 text-xs hover:underline"
              >
                Retry ({maxRetries - retryAttempts} left)
              </button>
            )}
          </div>
        </div>
      )
    }

    return isSupabaseUrl ? (
      <span className="relative inline-block max-w-full">
        {isLoading && (
          <span className="bg-muted/50 absolute inset-0 flex animate-pulse items-center justify-center rounded-lg">
            <span className="border-primary size-8 animate-spin rounded-full border-2 border-t-transparent" />
          </span>
        )}
        {nativeImage}
      </span>
    ) : (
      nativeImage
    )
  }

  // If it's a data URL or regular HTTP URL, use it directly
  if (isDataUrl || (isHttpUrl && !isSupabaseUrl)) {
    return (
      <Image
        src={currentSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        fill={fill}
        priority={priority}
        loading={loading}
        style={style}
        onError={onError}
        onLoad={onLoad}
        onClick={onClick}
      />
    )
  }

  return (
    <div className={`relative ${className || ''}`}>
      {isLoading && (
        <div className="bg-muted/50 absolute inset-0 flex animate-pulse items-center justify-center">
          <div className="border-primary size-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      )}

      {hasError ? (
        <div className="bg-muted/50 text-muted-foreground flex items-center justify-center">
          {fallbackSrc ? (
            <Image
              src={fallbackSrc}
              alt={alt}
              width={width}
              height={height}
              fill={fill}
              priority={priority}
              loading={loading}
              style={style}
            />
          ) : (
            <div className="p-4 text-center">
              <div className="text-sm">Image unavailable</div>
              {retryAttempts < maxRetries && (
                <button
                  onClick={handleImageError}
                  className="text-primary mt-2 text-xs hover:underline"
                >
                  Retry ({maxRetries - retryAttempts} left)
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <Image
          src={currentSrc}
          alt={alt}
          width={width}
          height={height}
          fill={fill}
          priority={priority}
          loading={loading}
          style={style}
          onError={handleImageError}
          onLoad={handleImageLoad}
          onClick={onClick}
          className={isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-200'}
        />
      )}
    </div>
  )
}

export default ResilientImage
